import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';

import Image from 'modules/views/shared/Image';
import SignatureModal from 'modules/views/shared/SignatureModal/SignatureModal';
import { ReactComponent as EditIcon } from 'assets/icons/Edit.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/Plus.svg';

import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { uploadSignatureToS3, fetchWorkerDetails } from 'modules/state-mgmt/onboarding';
import { createGuid, prettifyErrorResponse } from 'utils';
import { getImageFormat } from 'utils/files';
import { useStyles } from './styles';

export interface ISignatureSectionProps {
  workerId: string;
  languageCode: string;
}

const SignatureSection: React.FC<ISignatureSectionProps> = ({ workerId, languageCode }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [showModal, setShowModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const signatureUrl = useAppSelector(state => (state.onboarding as any)?.signatureUrl as string | undefined);

  const onSaveSignature = async (signData: string) => {
    try {
      setIsUploading(true);
      const fileName = `${createGuid()}.${getImageFormat(signData)}`;
      await dispatch(uploadSignatureToS3({ workerId, languageCode, signData, fileName })).unwrap();
      await dispatch(fetchWorkerDetails({ workerId, languageCode })).unwrap();
      setShowModal(false);
    } catch (error) {
      enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <p className={classes.sectionTitle}>{t('myProfile.signature.sectionTitle')}</p>
      {signatureUrl && (
        <div className={classes.thumbnailWrapper}>
          <Image src={signatureUrl} alt={t('myProfile.signature.sectionTitle')} className={classes.thumbnail} />
        </div>
      )}
      <button className={classes.editLink} onClick={() => setShowModal(true)} disabled={isUploading}>
        {signatureUrl ? (
          <>
            <EditIcon className={classes.editIcon} />
            {t('myProfile.signature.editSignature')}
          </>
        ) : (
          <>
            <PlusIcon className={classes.editIcon} /> {t('myProfile.signature.addSignature')}
          </>
        )}
      </button>
      {showModal && (
        <SignatureModal
          isLoading={isUploading}
          saveLabel={t('myProfile.signature.save')}
          saveButtonStyleClass={classes.saveButton}
          onClose={() => setShowModal(false)}
          onProceed={onSaveSignature}
        />
      )}
    </>
  );
};

export default SignatureSection;
